import React, { useState, useEffect, useContext } from 'react'
import AppLayout from '../layouts/AppLayout'
import { AppContext } from '../contexts/AppContext'

const ReportInfringement = () => {
  const { getInfringingUrls, addInfringingUrls, isLoading, setIsLoading } = useContext(AppContext)
  const [urls, setUrls] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submittedUrls, setSubmittedUrls] = useState([])
  const [isValidUrls, setIsValidUrls] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [pagination, setPagination] = useState({})

  useEffect(() => {
    fetchSubmittedUrls(1, itemsPerPage)
  }, [itemsPerPage])

  // URL validation regex
  const urlRegex =
    /^https?:\/\/(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?::[0-9]+)?(?:\/[^\s]*)?(?:\?[^\s]*)?(?:#[^\s]*)?$/i

  const validateUrls = (urlText) => {
    if (!urlText.trim()) return true

    const urlLines = urlText
      .trim()
      .split('\n')
      .filter((line) => line.trim())

    if (urlLines.length === 0) return true

    return urlLines.every((line) => {
      const trimmedLine = line.trim()
      return trimmedLine === '' || urlRegex.test(trimmedLine)
    })
  }

  const handleUrlChange = (e) => {
    const newUrls = e.target.value
    setUrls(newUrls)
    setIsValidUrls(validateUrls(newUrls))
  }

  const fetchSubmittedUrls = async (page = currentPage, limit = itemsPerPage) => {
    try {
      const data = await getInfringingUrls(page, limit)
      setSubmittedUrls(data.urls)
      setPagination(data.pagination)
    } catch (error) {
      console.error('Failed to fetch submitted URLs:', error)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!urls.trim()) return

    setIsSubmitting(true)
    try {
      const result = await addInfringingUrls(urls.trim())
      if (result) {
        setUrls('')
        fetchSubmittedUrls(1, itemsPerPage) // Refresh the list from first page

        // Show feedback about duplicates if any
      }
    } catch (error) {
      console.error('Error submitting URLs:', error)
      alert('Error submitting URLs. Please try again.')
    } finally {
      setIsSubmitting(false)
      setIsLoading(false)
    }
  }

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage)
    fetchSubmittedUrls(newPage, itemsPerPage)
  }

  const handleItemsPerPageChange = (newLimit) => {
    setItemsPerPage(newLimit)
    setCurrentPage(1) // Reset to first page when changing items per page
    fetchSubmittedUrls(1, newLimit)
  }

  return (
    <AppLayout pageName='Report Infringement'>
      <div className='max-w-[95%] mx-auto p-6 space-y-6'>
        {/* First Card - Submit URLs */}
        <div className='bg-white rounded-lg shadow-md p-8'>
          <h2 className='text-2xl font-bold text-gray-900 mb-2'>Report Infringements</h2>
          <p className='text-gray-600 mb-6'>
            Found a pirated Pocket FM show? Paste the links below: Spotify, YouTube, Telegram channels, file storage
            (Terabox, Mega, Google Drive…), websites or apps. Our team reviews every link and files the takedown.
          </p>

          <div className='mb-4'>
            <h3 className='text-lg font-semibold text-gray-800 mb-3'>Infringing URLs</h3>
            <textarea
              value={urls}
              onChange={handleUrlChange}
              placeholder={'One URL per line, e.g.\nhttps://t.me/channel_name/123\nhttps://www.youtube.com/watch?v=...\nhttps://terabox.com/s/...'}
              className='w-full p-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none'
              rows={10}
            />
            {!isValidUrls && urls.trim() && (
              <p className='text-red-600 text-sm mt-2'>
                Please enter valid URLs. Each URL should start with http:// or https:// and be on a separate line.
              </p>
            )}
          </div>

          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !urls.trim() || !isValidUrls}
            className='bg-blue-600 text-white px-8 py-3 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
          >
            {isSubmitting ? 'Submitting...' : 'Submit Report'}
          </button>
        </div>

        {/* Second Card - Previously Submitted URLs */}
        <div className='bg-white rounded-lg shadow-md p-8'>
          <h2 className='text-2xl font-bold text-gray-900 mb-6'>Previously Submitted URLs</h2>

          {isLoading ? (
            <div className='text-center py-8'>
              <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto'></div>
              <p className='mt-2 text-gray-600'>Loading...</p>
            </div>
          ) : submittedUrls.length > 0 ? (
            <div className='overflow-x-auto'>
              <table className='min-w-full border-collapse border border-gray-300'>
                <thead>
                  <tr className='bg-gray-50'>
                    <th className='border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700'>URL</th>
                    <th className='border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700'>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {submittedUrls.map((item, index) => (
                    <tr
                      key={index}
                      className='hover:bg-gray-50'
                    >
                      <td className='border border-gray-300 px-4 py-3 text-gray-800'>
                        <a
                          href={item.url}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='text-blue-600 hover:underline break-all'
                        >
                          {item.url}
                        </a>
                      </td>

                      <td className='border border-gray-300 px-4 py-3 text-gray-600'>
                        {new Date(item.created_at).toLocaleString()} 
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className='text-center py-8 text-gray-500'>
              <p>No URLs have been submitted yet.</p>
            </div>
          )}

          {/* Pagination Controls */}
          {submittedUrls.length > 0 && pagination.totalPages && pagination.totalPages > 1 && (
            <div className='mt-6 flex flex-col sm:flex-row justify-between items-center gap-4'>
              {/* Items per page selector */}
              <div className='flex items-center gap-2'>
                <label htmlFor='itemsPerPage' className='text-sm text-gray-600'>
                  Items per page:
                </label>
                <select
                  id='itemsPerPage'
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                  className='border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>

              {/* Pagination info */}
              <div className='text-sm text-gray-600'>
                Showing {((pagination.currentPage || 1) - 1) * (pagination.itemsPerPage || itemsPerPage) + 1} to{' '}
                {Math.min((pagination.currentPage || 1) * (pagination.itemsPerPage || itemsPerPage), pagination.totalItems || 0)} of{' '}
                {pagination.totalItems || 0} results
              </div>

              {/* Page navigation */}
              <div className='flex items-center gap-2'>
                <button
                  onClick={() => handlePageChange((pagination.currentPage || 1) - 1)}
                  disabled={!pagination.hasPrevPage}
                  className='px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500'
                >
                  Previous
                </button>
                
                {/* Page numbers */}
                <div className='flex items-center gap-1'>
                  {Array.from({ length: Math.min(5, pagination.totalPages || 1) }, (_, i) => {
                    let pageNum
                    const totalPages = pagination.totalPages || 1
                    const currentPage = pagination.currentPage || 1
                    
                    if (totalPages <= 5) {
                      pageNum = i + 1
                    } else if (currentPage <= 3) {
                      pageNum = i + 1
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i
                    } else {
                      pageNum = currentPage - 2 + i
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-1 border rounded-md text-sm ${
                          pageNum === currentPage
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 hover:bg-gray-50'
                        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      >
                        {pageNum}
                      </button>
                    )
                  })}
                </div>

                <button
                  onClick={() => handlePageChange((pagination.currentPage || 1) + 1)}
                  disabled={!pagination.hasNextPage}
                  className='px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500'
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  )
}

export default ReportInfringement
